// ─────────────────────────────────────────────
//  providerCell — one sheet cell → the PEOPLE it names.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  A CRNA cell in the daily sheet is written for a human reader, so it carries
//  more than a name: a shift code, a request marker, a note, or a second person.
//  Taken whole it matched no staff record, and the importer minted a new person
//  for the whole string. That is how the live directory ended up holding
//  "Charlotte Sanchez 7a-7a", "Josh Larson 7 REQ", "Dave/Nick" and
//  "Hana Alsamman 7a-7p/Toni Abraham 7p-7a" as if they were colleagues.
//
//  ⚠️ THE SEPARATORS ARE "/" AND "," — NOT "&" AND "+". Measured against the
//  live staff collection on 2026-08-20: every real multi-name cell used a slash
//  or a comma, and not one used "&" or "+". Both are accepted anyway because
//  they cost nothing and a sheet is typed by hand, but do not mistake them for
//  the common case.
//
//  ⚠️ COMMA-SPLITTING ALREADY EXISTED — FOR OB ROOMS ONLY (the retired parseWesleyChapel's
//  buildObRows, for the "day/night pair" cell). Every other room kept the whole
//  string. That asymmetry is the bug: "Jim Slone 3, Gabi Swanson" in a Main OR
//  room minted one phantom, while the identical shape in WC OB split correctly.
//
//  ⚠️ ORIENTING PAIRS ARE NOT PARSED HERE. domain/orientation.js already owns
//  "X Orient Y" — a preceptor and an orientee are a SEAT with two people in it,
//  which is a different fact from two independent providers sharing a room, and
//  it carries its own staffing rules. This module defers to it rather than
//  re-deriving the split, so there is one definition of what "Orient" means.
//
//  Pure: no Firebase, no React. Everything here is name text in, name text out.
// ─────────────────────────────────────────────
import { parseOrientation } from "./orientation";
import {
  isEmptySegment,
  isNameToken,
  isPersonSegment,
  isRoutingToken,
  isShiftToken,
  normalizeSegment,
  personSegmentIssue,
  stripTimeNote,
} from "./personSegment";

// ⚠️ ONE VOCABULARY. The shift codes, the name-token shape, the routing words
// and the "is this side a person" test all live in domain/personSegment now,
// because the `orient` path used to answer that last question differently from
// the slash and paren paths — and the disagreement minted a colleague named
// `to OB floor`. Re-exported here so existing callers keep their import path.
export { isShiftToken, isNameToken, isPersonSegment, normalizeSegment, personSegmentIssue, stripTimeNote };

// The separators that mean "and another person". Slash and comma are what the
// sheet actually uses; & and + are accepted defensively.
const SPLIT_RE = /\s*[/,&+]\s*/;

// A shift window written the way the sheet writes it: "7a-7p", "7a-7a",
// "11p - 7a", "7a-11p".
const SHIFT_WINDOW_RE = /\b\d{1,2}\s*[ap]\s*-\s*\d{1,2}\s*[ap]\b/gi;

// Annotation words that ride along after a name and are NOT part of it.
// `orient` appears here for the DANGLING case only ("Anthony Cecere orient",
// where the sheet named a preceptor and never typed the orientee) — a real
// pair is handled by domain/orientation before this runs.
//
// ⚠️ `orientation` IS ITS OWN WORD. `\borient\b` cannot match inside it, so
// "Lester Pineda 3 orientation 7-8:30" — a note about ONE person's own
// morning, not a pair — kept the whole tail as part of his name.
const ANNOTATION_RE = /\b(req|orientation|orient(?:s|ing)?|interview|call|late|early|off|out|holiday|vacation|pto)\b.*$/i;

// A trailing shift code: a bare number, optionally preceded by a hyphen.
// Anchored to the END so "Carlos Villanueva 9" loses the 9 and a person whose
// NAME contains a numeral (none do here, but the rule should not depend on
// that) keeps everything before the final token.
const TRAILING_NUMBER_RE = /[\s-]+\d{1,2}\s*$/;

/**
 * Strip the annotations off ONE person's segment, leaving the name.
 *
 * Order matters: the shift WINDOW goes first (it contains digits that the
 * trailing-number rule would otherwise chew from the wrong end), then the
 * annotation tail, then a trailing bare shift number.
 */
export function cleanProviderName(segment) {
  let s = String(segment || "").trim();
  if (!s) return "";
  // The clock note goes FIRST: its digits are exactly what the trailing-number
  // rule at the end would otherwise chew from the wrong side.
  s = stripTimeNote(s);
  s = s.replace(SHIFT_WINDOW_RE, " ");
  s = s.replace(ANNOTATION_RE, " ");
  s = s.replace(TRAILING_NUMBER_RE, "");
  // Collapse whatever whitespace the removals left behind, and drop stray
  // punctuation the annotations were attached with.
  s = s.replace(/\s+/g, " ").replace(/^[\s,;/&+-]+|[\s,;/&+-]+$/g, "").trim();
  return s;
}

/** True when a segment names nobody at all. */
export const isEmptyProvider = (segment) => isEmptySegment(segment);

// ─────────────────────────────────────────────
//  A CELL THAT NAMES TWO PEOPLE WITHOUT SAYING "ORIENT"
//
//  Measured on the owner's live board, 2026-08-20, in the 3 PM adequacy card:
//      Yarsellis Frometa 5/Ti…        (truncated — "…/Titi Somade 3")
//  and recorded earlier in the live staff collection, where the importer had
//  minted each of these as a COLLEAGUE:
//      Hana Alsamman 7a-7p/Toni Abraham 7p-7a
//      Adam Batz 3 (Samantha Noriega 3)
//      Matt Palsgrove 5 (Nicole Olsson 7)
//  Two CRNAs in one room, printed as one entry, counted once — the same defect
//  as the "X orient Y" cell, in the two other spellings the sheet uses.
//
//  ⚠️ THE SEPARATOR IS NOT A DISCRIMINATOR, AND THAT IS THE WHOLE PROBLEM.
//  The routing annotation this parser already supports is "Victoria Gordeen 3
//  to/from CIE6" — a slash between a direction and a ROOM. Splitting on the
//  character alone turns one CRNA and a room into two people, and a fabricated
//  provider is worse than the undercount it replaces: the board then claims
//  cover from somebody who does not exist and cannot be called.
//
//  ⚠️ SO THE TEST IS ON THE SIDES, NOT THE SEPARATOR, AND IT FAILS CLOSED.
//  Every side must look like a PERSON — two or more name-shaped words, with at
//  most their own trailing shift token — or the cell is left exactly as it is
//  today. Measured against the real board on 2026-08-20, that keeps whole:
//      "Dave/Nick"                    one word a side, and neither resolves
//      "Victoria Gordeen 3 to/from CIE6"   routing word, and a room code
//      "Someone 5 (relief)"           one word, not a name
//      "INO (PC 3/4)"                 a digit inside the token
//  and splits only the four shapes above.
// ─────────────────────────────────────────────

/**
 * Does this single token ANNOTATE a name rather than belong to it?
 *
 * ⚠️ ONE VOCABULARY, COMPOSED — NOT A NEW ONE. Every word this answers "yes"
 * to is already defined above: the shift codes (`isShiftToken` → domain/schedule
 * SHIFT_CODES), the routing / bookkeeping words (domain/personSegment), and the
 * annotation tail (`ANNOTATION_RE`). This repo has been bitten by parallel
 * copies of exactly this list, so a caller that needs to recognise "REQ",
 * "relief" or "orient" at the end of a name imports THIS instead of retyping it.
 *
 * Surrounding punctuation is peeled first, because the sheet brackets these
 * words as often as it does not: "(relief)", "7 REQ.", "- orient".
 */
export function isAnnotationToken(token) {
  const t = String(token || "").trim().replace(/^[^A-Za-z0-9@]+|[^A-Za-z0-9]+$/g, "");
  if (!t) return true; // pure punctuation names nobody
  return isShiftToken(t) || isRoutingToken(t) || ANNOTATION_RE.test(t);
}

/** Could this token be a WORD OF SOMEBODY'S NAME rather than bookkeeping? */
export const isNameCandidate = (token) => isNameToken(token) && !isAnnotationToken(token);

/**
 * The words of `name` with the sheet's TRAILING ANNOTATIONS peeled off.
 *
 * ⚠️ WHAT THIS EXISTS TO STOP: "the last word" being taken as the surname when
 * the last word is "REQ". A display that shortens a long name to its surname
 * ("K. Grant 7 REQ" → "K. REQ") prints a scheduling marker wearing a person's
 * shape, and on a wall board that is worse than truncating: "Klaphak…"
 * announces itself as incomplete, "REQ" does not.
 *
 * TWO FLOORS, because the two kinds of annotation carry different risk:
 *
 *   HARD — a token that CANNOT be part of a name: it holds a digit or brackets
 *   ("7", "(relief)", "7a-7p", "7-8:30"). Nothing is lost, so it is peeled down
 *   to a single surviving token.
 *
 *   SOFT — a name-SHAPED word this sheet uses as bookkeeping ("REQ", "orient",
 *   "relief"). "Early" and "Holiday" are also real surnames, so this peel stops
 *   with TWO words left: a person is a given name and a surname, and no peel may
 *   leave the surname slot holding the given name.
 *
 * ⚠️ AND A CELL THAT IS *ONLY* ANNOTATIONS KEEPS EVERY WORD. "REQ 7" names
 * nobody, but a blank where a name belongs is the one outcome worse than an odd
 * one — the reader cannot even tell the room has a problem. Callers get the
 * original words back and print them.
 */
export function nameWordsOf(name) {
  const t = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (t.length <= 1) return t;
  if (!t.some(isNameCandidate)) return t;
  let i = t.length;
  while (i > 1) {
    const last = t[i - 1];
    if (!isNameToken(last)) { i -= 1; continue; }                // HARD — floor of one
    if (i > 2 && isAnnotationToken(last)) { i -= 1; continue; }  // SOFT — floor of two
    break;
  }
  return t.slice(0, i);
}

/**
 * One person segment → their name and their OWN shift code.
 *
 * ⚠️ THEIR OWN. "Hana Alsamman 7a-7p/Toni Abraham 7p-7a" is two different
 * leaving times, and that is the entire point of carrying a second provider —
 * a room with two CRNAs loses them at two different hours.
 */
export function splitNameAndShift(segment) {
  const s = String(segment || "").trim();
  if (!s) return { name: "", shiftCode: null };
  const tokens = s.split(/\s+/).filter(Boolean);
  const last = tokens[tokens.length - 1];
  if (tokens.length > 1 && isShiftToken(last)) {
    return { name: cleanProviderName(tokens.slice(0, -1).join(" ")), shiftCode: last };
  }
  return { name: cleanProviderName(s), shiftCode: null };
}

// A trailing parenthesis holding a whole person: "Adam Batz 3 (Samantha
// Noriega 3)". Nested parens are excluded, so "(PC 3/4)" style annotations that
// happen to contain one cannot be mistaken for a name.
const TRAILING_PAREN_RE = /^(.*\S)\s*\(([^()]+)\)\s*$/;

/**
 * A cell that names a SECOND provider without the word "orient" → the raw
 * segments, in the order written. `null` when it names one person, or when any
 * side is anything but a person.
 *
 * Raw segments, deliberately: the sheet parser runs its own full pipeline
 * (routing annotations, shift vocabulary) over each one, and handing it a
 * pre-cleaned name would be a second definition of what a shift code is.
 *
 * ⚠️ NOT FOR "X ORIENT Y". domain/orientation owns that spelling and carries
 * the teaching relationship with it; callers ask it FIRST.
 */
export function coProviderSegments(cell) {
  const raw = String(cell || "").trim();
  if (!raw || isEmptyProvider(raw)) return null;

  if (raw.includes("/")) {
    const parts = raw.split("/").map((p) => p.trim());
    if (parts.length >= 2 && parts.every((part) => isPersonSegment(part))) return parts;
    // Ambiguous, so unchanged. "to/from CIE6" lands here, and so does anything
    // this rule has not been shown.
    return null;
  }

  const paren = raw.match(TRAILING_PAREN_RE);
  if (paren) {
    const lead = paren[1].trim();
    const second = paren[2].trim();
    if (isPersonSegment(lead) && isPersonSegment(second)) return [lead, second];
  }
  return null;
}

/**
 * WHY a separator in this cell was NOT treated as "and another person".
 *
 * ⚠️ A REFUSAL HAS TO BE VISIBLE. `coProviderSegments` returning null is the
 * right answer and an invisible one: the cell stays whole, one name is written
 * for two people, and nothing anywhere says a decision was made. This turns
 * the same decision into a sentence a human can act on, which is what the
 * import's review queue prints. `null` means "no separator, nothing refused".
 */
export function coProviderIssue(cell) {
  const raw = String(cell || "").trim();
  if (!raw || isEmptyProvider(raw) || coProviderSegments(raw)) return null;
  if (raw.includes("/")) {
    const parts = raw.split("/").map((p) => p.trim());
    for (const part of parts) {
      const issue = personSegmentIssue(part);
      // "to/from CIE6" is a routing phrase the parser already understands —
      // not a refused pair, so it is not worth a human's attention.
      if (issue && issue.code === "routingWord") return null;
      if (issue) return { code: "slashNotAPair", detail: `${part} — ${issue.code}` };
    }
  }
  const paren = raw.match(TRAILING_PAREN_RE);
  if (paren && personSegmentIssue(paren[2].trim())) {
    return null; // a parenthesised ANNOTATION, handled by normalizeSegment
  }
  return null;
}

/**
 * A cell that names TWO people → the RAW segment for each of them, in the
 * order written, WHICHEVER of the sheet's spellings was used. `null` when the
 * cell names one person.
 *
 * ⚠️ THE ONE PLACE THAT ANSWERS "WHICH HALF OF THIS CELL IS WHOSE?". A caller
 * that wants a person's own shift token has to know where THEIR side of the
 * sentence starts and ends — and the four spellings put the boundary in four
 * different places:
 *
 *     "Trevor Stamand 5 orient Kayla Gibbons"   → ["Trevor Stamand 5", "Kayla Gibbons"]
 *     "Yarsellis Frometa 5/Titi Somade 3"       → ["Yarsellis Frometa 5", "Titi Somade 3"]
 *     "Adam Batz 3 (Samantha Noriega 3)"        → ["Adam Batz 3", "Samantha Noriega 3"]
 *     "Rebecca Sigler 3 orient Sam Gongora"     → ["Rebecca Sigler 3", "Sam Gongora"]
 *
 * Orientation is asked FIRST for the reason `splitProviderCell` documents: the
 * sheet writes "orients w/ Sam Gongora" and that "w/" carries a slash.
 *
 * Raw segments, deliberately — `splitNameAndShift` is what turns one side into
 * a name and that person's OWN code, and there is one definition of each.
 */
export function pairedSegments(cell) {
  const raw = String(cell || "").trim();
  if (!raw || isEmptyProvider(raw)) return null;
  const orient = parseOrientation(raw);
  if (orient) return [orient.preceptor, orient.orientee];
  const co = coProviderSegments(raw);
  return co && co.length >= 2 ? co : null;
}

/**
 * One provider cell → the list of PEOPLE it names, cleaned.
 *
 * Returns [] for an empty or placeholder cell. Returns one name for the common
 * case. Returns two or more when the cell names a pair, in the order written.
 *
 * An ORIENTING cell returns both people (preceptor first) by deferring to
 * domain/orientation — the caller that cares about the seat's structure should
 * use `parseOrientation` directly; this is for "who is in this room".
 */
export function splitProviderCell(cell) {
  const raw = String(cell || "").trim();
  if (isEmptyProvider(raw)) return [];

  // ⚠️ ORIENTATION IS TESTED ON THE WHOLE CELL FIRST, BEFORE SPLITTING. The
  // sheet writes "orients w/ Sam Gongora", and that "w/" contains a SLASH — one
  // of the separators below. Splitting first turned the pair into three people,
  // the middle one named "w". Ask the narrower parser before reaching for the
  // blunt one.
  const whole = parseOrientation(raw);
  if (whole) {
    return [whole.preceptor, whole.orientee]
      .map(cleanProviderName)
      .filter((n) => n && !isEmptyProvider(n));
  }

  const segments = raw.split(SPLIT_RE);
  const people = [];
  for (const seg of segments) {
    if (isEmptyProvider(seg)) continue;
    const pair = parseOrientation(seg);
    if (pair) {
      // The preceptor keeps its trailing shift code (orientation.js documents
      // that it leaves it on); clean both halves here.
      for (const half of [pair.preceptor, pair.orientee]) {
        const name = cleanProviderName(half);
        if (name && !isEmptyProvider(name)) people.push(name);
      }
      continue;
    }
    const name = cleanProviderName(seg);
    if (name && !isEmptyProvider(name)) people.push(name);
  }
  return people;
}

/**
 * The single provider name a legacy caller expects, or "" when the cell names
 * nobody. Where a cell names several people this returns the FIRST — the seat
 * holder — so a caller that has not been taught about pairs still shows a real
 * person rather than a concatenation of two.
 */
export function primaryProviderName(cell) {
  return splitProviderCell(cell)[0] || "";
}
